import type { EChartsType } from "echarts/core";

/** Viewport-space rect for anchoring the unit-picker menu below the y-axis badge. */
export type AxisNameAnchor = { x: number; y: number; width: number; height: number };

/**
 * Minimal duck-type for a ZRender display object — ECharts draws axis badges on a canvas via ZRender ("zr"),
 * not as DOM nodes. We only model the bits we need to locate the badge and map its box into viewport coords.
 */
type ZrElement = {
    parent?: ZrElement;
    /** Event hit-test wrapper; walk this before parent when resolving click targets. */
    __hostTarget?: ZrElement;
    /** ZRender annotation id; axis name labels use anid "name". */
    anid?: string;
    updateTransform?: () => void;
    getBoundingRect: () => { x: number; y: number; width: number; height: number };
    /** Map local element coords into the chart canvas pixel space (still relative to the chart div). */
    transformCoordToGlobal: (x: number, y: number) => number[];
};

/** True when `el` is the y-axis name badge for `yAxisIndex` (not some other clickable chart piece). */
function axisNameEventData(el: unknown, yAxisIndex: number): ZrElement | null {
    const z = el as Partial<ZrElement>;
    if (
        z.anid !== "name" ||
        typeof z.getBoundingRect !== "function" ||
        typeof z.transformCoordToGlobal !== "function"
    ) {
        return null;
    }
    // ECharts stashes normalized event metadata on a private __ec_inner_* property — not in public types.
    const innerKey =
        el && typeof el === "object" ? Object.keys(el).find((k) => k.startsWith("__ec_inner_")) : undefined;
    const eventData = innerKey
        ? (el as Record<string, { eventData?: { targetType?: string; yAxisIndex?: number } }>)[innerKey]?.eventData
        : undefined;
    // `anid` alone isn't enough when multiple y-axes exist — using index disambiguates left vs right badge.
    if (eventData?.targetType !== "axisName" || eventData.yAxisIndex !== yAxisIndex) return null;
    return el as ZrElement;
}

/**
 * Find the ZRender node for a y-axis name badge.
 * Prefer walking up from the click target; fall back to a full scene scan when the target chain is incomplete.
 */
function locateAxisName(chart: EChartsType, yAxisIndex: number, clickTarget: unknown): ZrElement | null {
    for (let el: unknown = clickTarget; el; el = (el as ZrElement).__hostTarget ?? (el as ZrElement).parent) {
        const match = axisNameEventData(el, yAxisIndex);
        if (match) return match;
    }
    // Click sometimes lands on a child/wrapper that never reaches the name node — traverse the whole zr scene.
    let found: ZrElement | null = null;
    chart.getZr().storage.traverse((candidate) => {
        if (!found) found = axisNameEventData(candidate, yAxisIndex);
    });
    return found;
}

/** Convert a ZRender badge box into viewport coordinates for MenuPopup anchoring. */
function toViewportAnchor(el: ZrElement, chartContainer: HTMLElement): AxisNameAnchor {
    el.updateTransform?.();
    const { x, y, width, height } = el.getBoundingRect();
    const { left, top } = chartContainer.getBoundingClientRect();
    const xs: number[] = [];
    const ys: number[] = [];
    // Transform all four corners — badge can be rotated/scaled; axis-aligned min/max gives a safe anchor rect.
    (
        [
            [x, y],
            [x + width, y],
            [x, y + height],
            [x + width, y + height],
        ] as const
    ).forEach(([lx, ly]) => {
        const [gx, gy] = el.transformCoordToGlobal(lx, ly);
        xs.push(left + gx);
        ys.push(top + gy);
    });
    const anchorX = Math.min(...xs);
    const anchorY = Math.min(...ys);
    return { x: anchorX, y: anchorY, width: Math.max(...xs) - anchorX, height: Math.max(...ys) - anchorY };
}

/**
 * Resolve a y-axis badge click into a viewport anchor for the unit picker.
 * ECharts exposes click params but not a stable DOM element for canvas-drawn axis names — this bridges that gap.
 */
export function getAxisNameAnchor(
    chart: EChartsType,
    yAxisIndex: number,
    clickTarget: unknown,
    chartContainer: HTMLElement,
): AxisNameAnchor | null {
    const el = locateAxisName(chart, yAxisIndex, clickTarget);
    return el ? toViewportAnchor(el, chartContainer) : null;
}
